import React, {useState, useEffect} from "react";
import {Skeleton, Box, Typography, Grid, Tooltip} from "@mui/material";
import {BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, ResponsiveContainer} from "recharts";
import DateRangePickerComponent from "../../common/CustomDateRangePicker";
import moment from "moment";
import useDashboard from "../../../hooks/useDashboard";
import EmptyIcon from "../../../assets/images/empty-box.png";
import useDashboardResetFilterStore from "../../../zustand/useDashboardResetFilterStore";

const colors = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', "#C309F1"];

const styles = {
    xAxisTick: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: 120 
    }
}
  
const CustomXAxisTick = ({x, y, payload}) => (
    <Tooltip title={payload.value} arrow>
        <text
            x={x}
            y={y}
            dy={16}
            textAnchor="middle"
            fill="#666"
            style={styles.xAxisTick}
            transform={`translate(0, ${-y})`}
        >
            {payload.value}
        </text>
    </Tooltip>
)

const getPath = (x, y, width, height) => {
    return `M${x},${y + height}C${x + width / 3},${y + height} ${x + width / 2},${y + height / 3}
    ${x + width / 2}, ${y}
    C${x + width / 2},${y + height / 3} ${x + (2 * width) / 3},${y + height} ${x + width}, ${y + height}
    Z`;
}

const TriangleBar = (props) => {
    const { fill, x, y, width, height } = props;

    return <path d={getPath(x, y, width, height)} stroke="none" fill={fill} />;
}


const TopProductsAnomaliesComponent = (props) => {
    const {breakpoints} = props;

    const {isDashboardResetFilters, setDashboardResetFilters} = useDashboardResetFilterStore();
    const {topProductAnomalies, isFetchingTopProductsAnomalies, getTopProductAnomalies} = useDashboard();

    const [startDate, setStartDate] = useState(moment().subtract(6, 'days').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
    const [topProductAnomaliesData, setTopProductAnomaliesData] = useState([]);
    

    useEffect(() => {
        if (isDashboardResetFilters) {
            setStartDate(moment().subtract(6, "days").format("YYYY-MM-DD"));
            setEndDate(moment().format("YYYY-MM-DD"));
        }
    }, [isDashboardResetFilters]);

    useEffect(()=> {
        if (startDate && endDate) { 
            getTopProductAnomalies(startDate, endDate);
            setDashboardResetFilters(false);
        }
    }, [startDate, endDate])

    useEffect(() => {
        if (topProductAnomalies) {
            const transformedData = topProductAnomalies?.map(item => ({
                name: item?.productInfo?.product_name || "Unknown",
                count: item?.count,
            }));

            setTopProductAnomaliesData(transformedData);
        }
    }, [topProductAnomalies])       

 
    const handleDateRangeChange = (startDate, endDate) => {
        setStartDate(startDate);
        setEndDate(endDate);
    }

    const CustomizedAxisTick = ({ x, y, payload }) => (
        <g transform={`translate(${x},${y})`}>
          <text x={0} y={0} dy={16} textAnchor="end" fill="#666" transform="rotate(-25)" style={{fontSize: 10}} >
            {payload.value}
          </text>
        </g>
      );

    return (
        <Box sx={{backgroundColor: "#fff", mt: "20px", pt: "24px", borderRadius: "8px", width: "95%", ml: 3, height: "465px"}}>
            <Grid container sx={{paddingLeft: "24px", paddingRight: "24px"}}>
                <Grid item xs={4.5} sm={4.5} md={4.5} lg={4.5} xl={4.5}>
                    <Typography sx={{color: "#58565f", fontSize: 16, mb: 3, fontWeight: 600}}>
                        Top Products with Anomalies
                    </Typography>
                </Grid>

                <Grid item xs={7.5} sm={7.5} md={7.5} lg={7.5} xl={7.5} sx={{textAlign: "end", justifyContent: "end", ml: "auto", display: "flex"}}>   
                    <DateRangePickerComponent 
                        initialStartDate={startDate}
                        initialEndDate={endDate}
                        ranges={{
                            "Last 24 Hours": [moment().subtract(1, 'day'), moment()],
                            "Last 7 Days": [moment().subtract(6, 'days'), moment()],
                            "This Month": [moment().startOf('month'), moment().endOf('month')],
                            "Last Month": [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
                            "Last 90 Days": [moment().subtract(89, 'days'), moment()],
                        }}
                        onDateRangeChange={handleDateRangeChange}
                    />
                </Grid>
            </Grid>

            {(isFetchingTopProductsAnomalies) ?
                <Box sx={{margin: "30px 30px 10px 30px"}}>
                    <Skeleton variant="rounded" sx={{backgroundColor: "#d8d8fdbd"}} height={300} />
                </Box>

            : 
                (!isFetchingTopProductsAnomalies && topProductAnomaliesData?.length > 0) ?
                    <ResponsiveContainer width="100%" height={406} style={{paddingBottom: "24px"}}>
                        <BarChart width={500} height={306} data={topProductAnomaliesData} maxHeight={385}
                            margin={{top: 20, right: 30, left: 20, bottom: 50}} 
                            className={"productAnomalies"}
                        >
                            <CartesianGrid strokeDasharray="3 3"  horizontal={true} vertical={false} />
                            <XAxis dataKey="name"  height={80} tick={<CustomizedAxisTick />} allowDuplicatedCategory={false} interval={0} />
                            <YAxis />

                            <Bar dataKey="count" fill="#8884d8" shape={<TriangleBar />} label={{position: "top"}}>
                                {topProductAnomaliesData?.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={colors[index % 20]} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
            :
                <Box sx={{textAlign: "center", p: 3, pt: (breakpoints == "xs" || breakpoints == "sm") ? {} : 10}}>
                    <img alt="empty-data-icon" src={EmptyIcon} width="150" />

                    <Typography sx={{color: "#58565f", fontSize: 14, mt: 1}}>
                        No data available for the selected date range
                    </Typography>
                </Box>
            }
        </Box>
    )

}

export default TopProductsAnomaliesComponent